import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import FilterListIcon from "@material-ui/icons/FilterList";
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import AutoRepliesTable from "../../components/AutoRepliesTable";
import AutoReplyDialog from "../../components/AutoReplyDialog";
import AutoReplyDeliveryDialog from "../../components/AutoReplyDeliveryDialog";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";
  const primary = theme.palette.primary.main;

  return {
    mainPaper: {
      flex: 1,
      padding: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      border: "none",
      borderRadius: 10,
      background: isDark ? "#424242" : "#f2f3ff",
      boxShadow: isDark
        ? "0 18px 42px rgba(0, 0, 0, 0.28)"
        : "0 18px 42px rgba(15, 23, 42, 0.08)",
    },
    overview: {
      padding: theme.spacing(3),
      background: isDark
        ? "#424242"
        : "linear-gradient(135deg, #ffffff 0%, #f2f6ff 58%, #e8efff 100%)",
      color: isDark ? "#f8fafc" : "#131b2e",
    },
    overviewTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    overviewTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    overviewIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 12px 24px rgba(0, 0, 0, 0.28)"
        : "0 12px 24px rgba(0, 82, 204, 0.22)",
    },
    overviewLabel: {
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    overviewTitle: {
      margin: 0,
      color: isDark ? "#f8fafc" : "#131b2e",
      fontSize: 28,
      lineHeight: 1.12,
      fontWeight: 800,
    },
    overviewText: {
      maxWidth: 650,
      margin: theme.spacing(1.5, 0, 0),
      color: isDark ? "#cbd5e1" : "#434654",
      fontSize: 14,
      lineHeight: 1.6,
    },
    overviewActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        alignItems: "stretch",
        flexDirection: "column",
      },
    },
    templateButton: {
      minHeight: 42,
      padding: theme.spacing(1, 2),
      borderRadius: 6,
      color: isDark ? "#dbeafe" : "#0040a2",
      borderColor: isDark
        ? "rgba(147, 197, 253, 0.42)"
        : "rgba(0, 82, 204, 0.42)",
      backgroundColor: isDark ? "rgba(0, 82, 204, 0.14)" : "#eef4ff",
      fontWeight: 700,
      textTransform: "none",
      "&:hover": {
        borderColor: primary,
        backgroundColor: isDark ? "rgba(0, 82, 204, 0.22)" : "#dae2ff",
      },
    },
    addButton: {
      minHeight: 42,
      padding: theme.spacing(1, 2),
      borderRadius: 6,
      fontWeight: 700,
      textTransform: "none",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 12px 24px rgba(0, 0, 0, 0.28)"
        : "0 12px 24px rgba(0, 82, 204, 0.24)",
      "&:hover": {
        background: isDark
          ? "linear-gradient(135deg, #39ace7 0%, #0052cc 100%)"
          : "linear-gradient(135deg, #00327f 0%, #0049b8 100%)",
        boxShadow: isDark
          ? "0 14px 28px rgba(0, 0, 0, 0.32)"
          : "0 14px 28px rgba(0, 82, 204, 0.28)",
      },
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    statCard: {
      minHeight: 84,
      padding: theme.spacing(1.5),
      borderRadius: 8,
      backgroundColor: isDark ? "#4e4e4e" : "#ffffff",
      boxShadow: isDark
        ? "0 12px 30px rgba(0, 0, 0, 0.22)"
        : "0 12px 30px rgba(19, 27, 46, 0.06)",
    },
    statHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.35,
      textTransform: "uppercase",
    },
    statIcon: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? "#bfdbfe" : "#0040a2",
      backgroundColor: isDark ? "rgba(0, 82, 204, 0.32)" : "#dae2ff",
    },
    statValue: {
      marginTop: theme.spacing(1),
      color: isDark ? "#f8fafc" : "#131b2e",
      fontSize: 26,
      lineHeight: 1,
      fontWeight: 800,
    },
    statHint: {
      marginTop: theme.spacing(0.75),
      color: isDark ? "#94a3b8" : "#737685",
      fontSize: 12,
    },
    content: {
      flex: 1,
      minHeight: 0,
      padding: theme.spacing(2),
      overflowY: "auto",
      backgroundColor: isDark ? "#424242" : "#f2f3ff",
      ...theme.scrollbarStyles,
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        alignItems: "stretch",
        flexDirection: "column",
      },
    },
    searchField: {
      flex: 1,
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#f8fafc" : "#131b2e",
        backgroundColor: isDark ? "#4e4e4e" : "#ffffff",
        "& fieldset": {
          borderColor: isDark ? "rgba(255, 255, 255, 0.22)" : "rgba(195, 198, 214, 0.55)",
        },
        "&:hover fieldset": {
          borderColor: isDark ? "rgba(147, 197, 253, 0.72)" : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        color: isDark ? "#93c5fd" : "inherit",
      },
    },
    filterChip: {
      height: 38,
      borderRadius: 999,
      backgroundColor: isDark ? "#0052cc" : "#e2e7ff",
      color: isDark ? "#ffffff" : "#344573",
      fontWeight: 700,
      "& .MuiChip-icon": {
        color: isDark ? "#ffffff" : "#344573",
      },
      [theme.breakpoints.down("sm")]: {
        justifyContent: "flex-start",
      },
    },
    loadMoreWrapper: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(2),
    },
    loadMoreButton: {
      borderRadius: 999,
      padding: theme.spacing(0.85, 2.5),
      color: isDark ? "#ffffff" : "#0040a2",
      backgroundColor: isDark ? "#0052cc" : "#dae2ff",
      fontWeight: 700,
      textTransform: "none",
      "&:hover": {
        backgroundColor: isDark ? "#0040a2" : "#c4d2ff",
      },
    },
  };
});

const AutoReplies = () => {
  const classes = useStyles();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    setRecords([]);
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, searchParam]);

  const fetchRecords = async ({ nextSearchParam, nextPageNumber, reset } = {}) => {
    const searchValue = nextSearchParam !== undefined ? nextSearchParam : searchParam;
    const pageValue = nextPageNumber !== undefined ? nextPageNumber : pageNumber;

    try {
      setLoading(true);
      const { data } = await api.get("/auto-replies", {
        params: { searchParam: searchValue, pageNumber: pageValue },
      });
      setRecords((prev) => (reset ? data.records : [...prev, ...data.records]));
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const reloadRecords = async () => {
    setRecords([]);
    setSearchParam("");
    setPageNumber(1);
    await fetchRecords({ nextSearchParam: "", nextPageNumber: 1, reset: true });
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
  };

  const handleOpenDialog = () => {
    setRecordId(null);
    setDialogOpen(true);
  };

  const handleOpenDeliveryDialog = () => {
    setDeliveryDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setRecordId(null);
    setDialogOpen(false);
    reloadRecords();
  };

  const handleEdit = (record) => {
    setRecordId(record.id);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/auto-replies/${deleting.id}`);
      toast.success(i18n.t("autoReplies.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    setConfirmOpen(false);
    reloadRecords();
  };

  const loadMore = () => {
    if (hasMore) setPageNumber((prev) => prev + 1);
  };

  const mediaCount = records.filter((record) => record.mediaPath || record.mediaName).length;
  const hasActiveFilter = Boolean(searchParam.trim());

  return (
    <MainContainer>
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title={i18n.t("autoReplies.confirmationModal.deleteTitle")}
        message={i18n.t("autoReplies.confirmationModal.deleteMessage")}
      />
      <AutoReplyDialog open={dialogOpen} onClose={handleCloseDialog} recordId={recordId} />
      <AutoReplyDeliveryDialog
        open={deliveryDialogOpen}
        onClose={() => setDeliveryDialogOpen(false)}
        onCreated={reloadRecords}
      />
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.overview}>
          <Box className={classes.overviewTop}>
            <Box>
              <Box className={classes.overviewTitleRow}>
                <Box className={classes.overviewIcon}>
                  <QuestionAnswerIcon />
                </Box>
                <Box>
                  <Box className={classes.overviewLabel}>Automacao do atendimento</Box>
                  <h2 className={classes.overviewTitle}>{i18n.t("autoReplies.title")}</h2>
                </Box>
              </Box>
              <p className={classes.overviewText}>
                Organize respostas por palavra-chave, condicao e conteudo para reduzir o tempo de atendimento sem perder contexto.
              </p>
            </Box>
            <Box className={classes.overviewActions}>
              <Button
                variant="outlined"
                startIcon={<RestaurantIcon />}
                onClick={handleOpenDeliveryDialog}
                className={classes.templateButton}
              >
                Usar frases Delivery
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                className={classes.addButton}
              >
                {i18n.t("autoReplies.buttons.add")}
              </Button>
            </Box>
          </Box>

          <Box className={classes.statsGrid}>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Total de respostas
                <Box className={classes.statIcon}>
                  <QuestionAnswerIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{records.length}</Box>
              <Box className={classes.statHint}>Itens carregados nesta consulta</Box>
            </Box>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Respostas com midia
                <Box className={classes.statIcon}>
                  <AttachFileIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{mediaCount}</Box>
              <Box className={classes.statHint}>Com arquivo vinculado</Box>
            </Box>
            <Box className={classes.statCard}>
              <Box className={classes.statHeader}>
                Filtro ativo
                <Box className={classes.statIcon}>
                  <FilterListIcon fontSize="small" />
                </Box>
              </Box>
              <Box className={classes.statValue}>{hasActiveFilter ? "Sim" : "Nao"}</Box>
              <Box className={classes.statHint}>
                {hasActiveFilter ? `Busca por "${searchParam}"` : "Mostrando todas as respostas"}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={classes.content}>
          <Box className={classes.toolbar}>
            <TextField
              className={classes.searchField}
              placeholder={i18n.t("autoReplies.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Chip
              className={classes.filterChip}
              icon={<FilterListIcon />}
              label={hasActiveFilter ? `Filtro: ${searchParam}` : "Todas as respostas"}
            />
          </Box>
          <AutoRepliesTable
            records={records}
            showLoading={loading}
            editRecord={handleEdit}
            deleteRecord={(record) => {
              setDeleting(record);
              setConfirmOpen(true);
            }}
          />
          {hasMore && !loading && (
            <Box className={classes.loadMoreWrapper}>
              <Button onClick={loadMore} className={classes.loadMoreButton}>
                {i18n.t("autoReplies.buttons.loadMore")}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default AutoReplies;
